// Whole-canvas voxel edits that aren't tool gestures: fill / clear, and the
// in-place rotate / flip of the selection. Pure document operations — each is
// one undo step wrapped in begin/endTransaction over the owned grid + selection,
// reading the live work-volume bounds. No main wiring: import and call.
// (The clipboard's copy/paste/duplicate is the sibling slice; the canvas
// TRANSFORM reset — gridGroup/world/camera — stays in main as a view concern.)

import { CELL_SIZE, type PaletteIndex } from "../../model/voxel-grid";
import {
  beginTransaction,
  endTransaction,
  recordPaletteChange,
  recordSnapshot,
} from "../../model/history";

// Fill/Clear mutate the WHOLE volume; recording one fast whole-grid snapshot for
// undo (instead of millions of per-cell records) is what keeps them snappy. main
// injects the capture fn (it owns the full ModelSnapshot — grid + bounds + unit).
let captureSnapshot: (() => unknown) | null = null;
export function setCanvasSnapshotCapture(fn: () => unknown): void {
  captureSnapshot = fn;
}
import { uiState } from "../../ui/state";
import {
  PALETTE_SIZE,
  colorInt,
  factoryColor,
  setColorInt,
  snapshotPalette,
} from "../../ui/palette";
import { volumeBounds, cellBounds } from "../../scene/work-volume-bounds";
import { grid } from "./grid";
import { selection } from "./selection";

// Fill the whole work volume with the current palette colour — one undo step.
// Bulk grid write + snapshot undo: no per-cell loop, no per-cell history.
export function fillCanvas(): void {
  const x0 = Math.round(volumeBounds.minX / CELL_SIZE);
  const x1 = Math.round(volumeBounds.maxX / CELL_SIZE);
  const z0 = Math.round(volumeBounds.minZ / CELL_SIZE);
  const z1 = Math.round(volumeBounds.maxZ / CELL_SIZE);
  const y1 = Math.round(volumeBounds.height / CELL_SIZE);
  const p = uiState.currentPaletteIndex;
  if (p === 0 || x1 <= x0 || y1 <= 0 || z1 <= z0) return;
  const before = captureSnapshot?.();
  beginTransaction(); // captures the selection before-state
  grid.fillBox(x0, x1, 0, y1, z0, z1, p);
  selection.replace([]); // cells changed; the old selection no longer maps
  if (before !== undefined) recordSnapshot(before, captureSnapshot!());
  endTransaction();
}

// Clear every voxel from the canvas — one undo step (no-op if already empty).
export function clearCanvas(): void {
  if (!grid.hasAnyCells()) return;
  const before = captureSnapshot?.();
  beginTransaction();
  grid.clear();
  selection.replace([]);
  if (before !== undefined) recordSnapshot(before, captureSnapshot!());
  endTransaction();
}

// Rotate the selected voxels in place by `deg` about a grid axis (0=X,1=Y,2=Z),
// re-voxelizing into the grid — the way to build an angled feature: model it
// straight, select it, then rotate it to the angle you want. The pivot is the
// selection's bounding-box centre. Resampling is inverse nearest-neighbour, so
// thin (1-voxel) features may need thickening first. One undo step.
export function rotateSelection(axis: 0 | 1 | 2, deg: number): void {
  if (selection.size() === 0) return;
  const norm = ((deg % 360) + 360) % 360;
  if (norm === 0) return;

  const srcKeys = new Set<string>();
  const srcPal = new Map<string, PaletteIndex>();
  let minX = Infinity;
  let minY = Infinity;
  let minZ = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  let maxZ = -Infinity;
  for (const c of selection.entries()) {
    const p = grid.get(c.ix, c.iy, c.iz);
    if (p === 0) continue;
    const k = `${c.ix},${c.iy},${c.iz}`;
    srcKeys.add(k);
    srcPal.set(k, p);
    minX = Math.min(minX, c.ix);
    minY = Math.min(minY, c.iy);
    minZ = Math.min(minZ, c.iz);
    maxX = Math.max(maxX, c.ix);
    maxY = Math.max(maxY, c.iy);
    maxZ = Math.max(maxZ, c.iz);
  }
  if (srcKeys.size === 0) return;

  // Pivot = bbox centre in continuous cell coords (cell centres sit at +0.5).
  const px = (minX + maxX + 1) / 2;
  const py = (minY + maxY + 1) / 2;
  const pz = (minZ + maxZ + 1) / 2;
  const rad = (norm * Math.PI) / 180;
  const c = Math.cos(rad);
  const s = Math.sin(rad);
  // Forward (+θ) and inverse (−θ) rotation about the chosen axis, around pivot.
  const rot = (x: number, y: number, z: number, sin: number): [number, number, number] => {
    let dx = x - px;
    let dy = y - py;
    let dz = z - pz;
    if (axis === 0) {
      const ny = dy * c - dz * sin;
      const nz = dy * sin + dz * c;
      dy = ny;
      dz = nz;
    } else if (axis === 1) {
      const nx = dx * c + dz * sin;
      const nz = -dx * sin + dz * c;
      dx = nx;
      dz = nz;
    } else {
      const nx = dx * c - dy * sin;
      const ny = dx * sin + dy * c;
      dx = nx;
      dy = ny;
    }
    return [dx + px, dy + py, dz + pz];
  };

  // Target bbox = forward-rotate the source bbox corners; clamp to the volume.
  let tMinX = Infinity;
  let tMinY = Infinity;
  let tMinZ = Infinity;
  let tMaxX = -Infinity;
  let tMaxY = -Infinity;
  let tMaxZ = -Infinity;
  for (const x of [minX, maxX + 1]) {
    for (const y of [minY, maxY + 1]) {
      for (const z of [minZ, maxZ + 1]) {
        const [rx, ry, rz] = rot(x, y, z, s);
        tMinX = Math.min(tMinX, rx);
        tMinY = Math.min(tMinY, ry);
        tMinZ = Math.min(tMinZ, rz);
        tMaxX = Math.max(tMaxX, rx);
        tMaxY = Math.max(tMaxY, ry);
        tMaxZ = Math.max(tMaxZ, rz);
      }
    }
  }
  const lo = (v: number, min: number) => Math.max(min, Math.floor(v));
  const hi = (v: number, max: number) => Math.min(max, Math.ceil(v));
  const x0 = lo(tMinX, cellBounds.minIx);
  const x1 = hi(tMaxX, cellBounds.maxIx);
  const y0 = lo(tMinY, cellBounds.minIy);
  const y1 = hi(tMaxY, cellBounds.maxIy);
  const z0 = lo(tMinZ, cellBounds.minIz);
  const z1 = hi(tMaxZ, cellBounds.maxIz);

  // Inverse-map each target cell centre back to a source cell.
  const out: { ix: number; iy: number; iz: number; p: PaletteIndex }[] = [];
  for (let tx = x0; tx < x1; tx++) {
    for (let ty = y0; ty < y1; ty++) {
      for (let tz = z0; tz < z1; tz++) {
        const [sx, sy, sz] = rot(tx + 0.5, ty + 0.5, tz + 0.5, -s);
        const key = `${Math.floor(sx)},${Math.floor(sy)},${Math.floor(sz)}`;
        const p = srcPal.get(key);
        if (p !== undefined) out.push({ ix: tx, iy: ty, iz: tz, p });
      }
    }
  }

  beginTransaction();
  for (const k of srcKeys) {
    const [ix, iy, iz] = k.split(",").map(Number);
    grid.delete(ix, iy, iz);
  }
  for (const o of out) grid.set(o.ix, o.iy, o.iz, o.p);
  selection.replace(out.map((o) => ({ ix: o.ix, iy: o.iy, iz: o.iz })));
  endTransaction();
}

// Flip (mirror) the selection in place, reflecting it about its bounding-box
// centre on `axis`. A flip is an exact 1:1 cell mapping (no resampling), so it's
// lossless. One undo step; the selection follows the flipped cells.
export function flipSelection(axis: 0 | 1 | 2): void {
  if (selection.size() === 0) return;
  const cells: { ix: number; iy: number; iz: number; p: PaletteIndex }[] = [];
  let min = Infinity;
  let max = -Infinity;
  for (const c of selection.entries()) {
    const p = grid.get(c.ix, c.iy, c.iz);
    if (p === 0) continue; // a selection can outlive its voxels; skip empties
    const coord = axis === 0 ? c.ix : axis === 1 ? c.iy : c.iz;
    if (coord < min) min = coord;
    if (coord > max) max = coord;
    cells.push({ ix: c.ix, iy: c.iy, iz: c.iz, p });
  }
  if (cells.length === 0) return;
  const sum = min + max; // reflection maps coord → (min + max) − coord
  const out = cells.map((c) => ({
    ix: axis === 0 ? sum - c.ix : c.ix,
    iy: axis === 1 ? sum - c.iy : c.iy,
    iz: axis === 2 ? sum - c.iz : c.iz,
    p: c.p,
  }));
  beginTransaction();
  for (const c of cells) grid.delete(c.ix, c.iy, c.iz);
  for (const o of out) grid.set(o.ix, o.iy, o.iz, o.p);
  selection.replace(out.map((o) => ({ ix: o.ix, iy: o.iy, iz: o.iz })));
  endTransaction();
}

// ── Swatch recolor (preserve existing voxels) ──────────────────────────────
// Recoloring a swatch must NOT recolor voxels already painted with it — only
// future painting picks up the new color. Voxels store palette INDICES, so we
// FREEZE the existing ones: move every voxel of the edited index to a fresh
// "frozen" slot (index > PALETTE_SIZE) holding the OLD color, then point the
// swatch at the new color. The re-index + the palette change commit as ONE undo
// step (the palette before/after rides on the same diff) so Ctrl+Z restores both
// together. Caller refreshes meshers/swatches afterward (refreshPaletteColors).

// Apply a batch of swatch recolors {swatchIndex → new sRGB int} atomically.
function applySwatchRecolors(targets: Map<number, number>): void {
  const before = snapshotPalette();

  // One pass over the grid: which target indices are actually painted (and
  // where), plus every index in use (so frozen-slot allocation avoids them).
  const usedIndices = new Set<PaletteIndex>();
  const positionsByIndex = new Map<number, Array<[number, number, number]>>();
  for (const [ix, iy, iz, p] of grid.entries()) {
    usedIndices.add(p);
    if (targets.has(p)) {
      let arr = positionsByIndex.get(p);
      if (!arr) {
        arr = [];
        positionsByIndex.set(p, arr);
      }
      arr.push([ix, iy, iz]);
    }
  }

  // Hand out the lowest free index above the swatch range, skipping any index a
  // voxel already uses. Null once the 1..255 range is exhausted (pathological).
  let next = PALETTE_SIZE + 1;
  const allocFrozen = (): number | null => {
    while (next <= 255 && usedIndices.has(next)) next++;
    if (next > 255) return null;
    const f = next;
    usedIndices.add(f); // reserve it for this batch
    next++;
    return f;
  };

  let changed = false;
  beginTransaction();
  for (const [i, newInt] of targets) {
    const old = colorInt(i);
    if (old === newInt) continue;
    const positions = positionsByIndex.get(i);
    if (positions && positions.length > 0) {
      const frozen = allocFrozen();
      if (frozen !== null) {
        setColorInt(frozen, old); // the frozen slot keeps the old appearance
        for (const [ix, iy, iz] of positions) grid.set(ix, iy, iz, frozen);
      }
      // frozen === null: palette space exhausted — fall through and recolor in
      // place (existing voxels change). Vanishingly rare; better than failing.
    }
    setColorInt(i, newInt); // swatch paints the new color going forward
    changed = true;
  }
  if (changed) recordPaletteChange(before, snapshotPalette());
  endTransaction();
}

// Recolor one swatch to `newColorInt` (an sRGB int), preserving existing voxels.
export function recolorSwatch(swatchIndex: number, newColorInt: number): void {
  if (swatchIndex < 1 || swatchIndex > PALETTE_SIZE) return;
  applySwatchRecolors(new Map([[swatchIndex, newColorInt]]));
}

// Reset every swatch to its factory default, preserving existing voxels (each
// non-default swatch in use is frozen first, exactly like a manual recolor).
export function resetSwatches(): void {
  const targets = new Map<number, number>();
  for (let i = 1; i <= PALETTE_SIZE; i++) {
    const def = factoryColor(i);
    if (colorInt(i) !== def) targets.set(i, def);
  }
  if (targets.size > 0) applySwatchRecolors(targets);
}
